/**
 * `GET /v1/me/bootstrap` — appskallets seks første spørsmål i ett kall.
 *
 * Hver fulle sidelast stilte de samme seks GET-ene i parallell (`/me`, `/me/profile`,
 * `/me/concerts`, uleste tråder, uleste varsler og festivallista). Målt i testmiljøet kostet hver
 * 150–250 ms, nesten alt per-forespørsel-overhead, og de seks konkurrerte om en pool på åtte
 * forbindelser. Her er de én forespørsel, én transaksjon og én klient.
 *
 * HVER DEL ER NØYAKTIG SVARET DET EGNE ENDEPUNKTET GIR, med samme skjema. Nettleseren legger delene
 * rett i de eksisterende cachenøklene, så krokene som leser dem virker uendret. Derfor finnes ingen
 * ny form her — bare en konvolutt rundt fem som finnes.
 *
 * `null` BETYR «SPØR SELV», ALDRI «TOMT». En del er null når cella mangler (rollen har ikke
 * `festival:list`), når det ikke finnes en aktør (ingen organisasjon, samtykke ute, fryst hus), og
 * for varseltallet til en gjest — klokka spør personens EGEN organisasjon, ikke den som delte.
 * Komponenten som trenger tallet henter det da selv, og får det svaret endepunktet alltid har gitt.
 */
import { z } from 'zod';

import { MAX_PAGE_SIZE } from '../http/pagination.js';
import { concertListSchema } from './concerts.js';
import { festivalListResponseSchema } from './festivals.js';
import { inboxUnreadCountsResponseSchema } from './inbox.js';
import { notificationUnreadCountSchema } from './notifications.js';
import { meResponseSchema, myProfileDtoSchema } from './users.js';

/**
 * Festivallista slik sidemenyen ber om den. ÉN konstant for begge sider, for svaret som legges i
 * `qk.festivalList(orgId)` må være det samme som nøkkelens egen spørring ville fått — en annen
 * `limit` her ville gitt en kortere meny til neste henting, uten at noe feilet.
 */
export const SHELL_FESTIVAL_LIST_QUERY = {
  sort: 'order',
  direction: 'asc',
  limit: MAX_PAGE_SIZE,
} as const;

export const meBootstrapResponseSchema = z
  .object({
    me: meResponseSchema,
    profile: myProfileDtoSchema,
    concerts: concertListSchema.nullable(),
    inboxUnreadCounts: inboxUnreadCountsResponseSchema.nullable(),
    notificationUnreadCount: notificationUnreadCountSchema.nullable(),
    festivals: festivalListResponseSchema.nullable(),
  })
  .meta({ id: 'MeBootstrapResponse' });

export type MeBootstrapResponse = z.infer<typeof meBootstrapResponseSchema>;
